import './docButtonRow.scss';
import {
    useSearchURL,
    useSelectedDocContext,
    useStateCurrentPageNumber,
    useStateResultDetailsTab
} from '../../../search-hooks';
import React, {useCallback, useEffect, useState} from 'react';
import {Icons} from '../../../../icons/icons';
import {downloadFile, parseURI} from '../../../search.utils';
import {DefaultRootState, useDispatch, useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {HibuOverlay} from '../../../../common/hibuOverlay/hibuOverlay';
import {HibuLink} from '../../../../common/hibuButton/hibuLink';
import {URL_FILTER_PARAM, URL_PAGE_PARAM} from '../../../const';
import {Dropdown} from 'react-bootstrap';
import {SvgIcon} from '../../../../icons/svgIcon';
import {encodeTab} from '../../../url/tab-type-parse';
import {useStateTechnicalDetails} from '../../../../settings/duck/settings-hooks';
import {ProductDocument} from '../../../search-types';

type DocButtonRowProps = {
    hasSearchQuery?: boolean
}
export const DocButtonRow = ({hasSearchQuery}: DocButtonRowProps): React.JSX.Element | null => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const doc: ProductDocument | undefined = useSelectedDocContext();
    const pageNumber = useStateCurrentPageNumber();
    const searchURL = useSearchURL();
    const showTechnicalDetails = useStateTechnicalDetails();
    const {
        showDownloadButton,
        showLinkToClipboardButton,
        showLinkByMailButton
    } = useSelector((state: DefaultRootState) => state.config.uiConfig.search.documentActions.toggles);

    const [documentLink, setDocumentLink] = useState('');
    const [mailSubject, setMailSubject] = useState('');
    const [mailBody, setMailBody] = useState('');
    const linkToClipboardKey = 'search.label.detailButtons.tooltips.linkToClipboard';
    const contentToClipboardKey = 'search.label.detailButtons.tooltips.copyContent';
    const contentUriToClipboardKey = 'search.label.detailButtons.tooltips.contentUriToClipboard';
    const copiedKey = 'search.label.detailButtons.tooltips.copied';
    const [linkToClipboardComputedKey, setLinkToClipboardComputedKey] = useState(linkToClipboardKey);
    const [contentToClipboardComputedKey, setContentToClipboardComputedKey] = useState(contentToClipboardKey);
    const [contentUriToClipboardComputedKey, setContentUriToClipboardComputedKey] = useState(contentUriToClipboardKey);
    const [contentUri, setContentUri] = useState<string | undefined>(undefined);
    const openTab = useStateResultDetailsTab();

    const updateDocumentDetails = useCallback(doc => {
        const filter = `?${URL_FILTER_PARAM}=id[${doc?.id}]&${URL_PAGE_PARAM}=${pageNumber}&${encodeTab(openTab)}`;
        setDocumentLink(encodeURI(`${searchURL}${filter}`));
        setMailSubject(t('search.share.link.mailSubject', {documentTitle: doc.title}));
        setMailBody(t('search.share.link.mailBody', {documentTitle: doc.title}) + documentLink);
    }, [documentLink, pageNumber, searchURL, t, openTab]);

    useEffect(() => doc && updateDocumentDetails(doc),
        [doc, updateDocumentDetails]
    );

    useEffect(() => {
        const contentUri: string =
            doc?.hibuTags?.contentUri?.[0]?.extra?.value_tokenized_string ??
            doc?.metadata?.contentUri_string;

        if (contentUri) {
            setContentUri(contentUri);
        }
    }, [doc?.hibuTags?.contentUri, doc?.metadata?.contentUri_string]);

    const copyTextToClipboard = async (val: string): Promise<void> => {
        return await navigator.clipboard.writeText(val);
    };

    const sendLinkByMail = useCallback(() => {
        const subject = parseURI('subject', mailSubject, true);
        const body = parseURI('body', mailBody, true);
        window.location.href = `mailto:?${subject}&${body}`;
    }, [mailBody, mailSubject]);

    if (!doc) return null;

    const renderLinkToClipboardButton = (): React.JSX.Element => {
        return (showLinkToClipboardButton ?
                <HibuOverlay
                    className={'document-button'}
                    onClick={() => {
                        copyTextToClipboard(documentLink).then(() => setLinkToClipboardComputedKey(copiedKey));
                    }}
                    resetFunction={() =>
                        setLinkToClipboardComputedKey(linkToClipboardKey)}
                    icon={Icons.CLOUD_LINK}
                    tooltipKey={linkToClipboardComputedKey}
                    size={'md'}
                /> :
                <></>
        );
    };

    const renderLinkByMailButton = (): React.JSX.Element => {
        return (showLinkByMailButton ?
                <HibuOverlay
                    className={'document-button'}
                    onClick={sendLinkByMail}
                    icon={Icons.MAIL}
                    tooltipKey={'search.label.detailButtons.tooltips.linkToMail'}
                    size={'md'}
                /> :
                <></>
        );
    };

    const renderDownloadFileButton = (): React.JSX.Element => {
        return (showDownloadButton && doc.metadata.downloadLink_string ?
                <HibuOverlay
                    className={'document-button'}
                    onClick={() => downloadFile(doc, dispatch)} // conditionally /previewFile called. This is for downloading the file
                    icon={Icons.DOWNLOAD}
                    tooltipKey={'search.label.detailButtons.tooltips.downloadFile'}
                    size={'md'}
                /> :
                <></>
        );
    };

    const renderContentToClipboardButton = (): React.JSX.Element => {
        return (showTechnicalDetails && doc?.content ?
                <HibuOverlay
                    className={'document-button'}
                    onClick={() => {
                        copyTextToClipboard(doc?.content ? doc.content : '').then(() => setContentToClipboardComputedKey(copiedKey));
                    }}
                    resetFunction={() => setContentToClipboardComputedKey(contentToClipboardKey)}
                    icon={Icons.CLIPBOARD}
                    tooltipKey={contentToClipboardComputedKey}
                    size={'md'}
                /> :
                <></>
        );
    };

    const renderContentUriToClipboardButton = (): React.JSX.Element => {
        return (contentUri ?
                <>
                    {
                        contentUri.startsWith('http') ? (
                                <HibuLink
                                    className={'document-button'}
                                    href={contentUri}
                                    icon={Icons.OPEN_IN}
                                    tooltipKey={'search.label.detailButtons.tooltips.cloudLink'}
                                    visible
                                    size={'md'}
                                />
                            ) :
                            (
                                <HibuOverlay //here
                                    className={'document-button'}
                                    onClick={() => {
                                        copyTextToClipboard(contentUri).then(() => setContentUriToClipboardComputedKey(copiedKey));
                                    }}
                                    resetFunction={() =>
                                        setContentUriToClipboardComputedKey(contentUriToClipboardKey)}
                                    icon={Icons.OPEN_IN}
                                    tooltipKey={contentUriToClipboardComputedKey}
                                    size={'md'}
                                />
                            )
                    }
                </> :
                <></>
        );
    };

    const renderButtons = (): React.JSX.Element => {
        return (
            <div className={`preview-header-group document-buttons ${hasSearchQuery && 'adapt_small_screen'}`}>
                {renderLinkToClipboardButton()}
                {renderLinkByMailButton()}
                {renderDownloadFileButton()}
                {renderContentToClipboardButton()}
                {renderContentUriToClipboardButton()}
                <></>
            </div>
        );
    };

    return (
        <>
            <Dropdown id={'doc-buttons-vertical'}
                      className={`small_screen_only ${hasSearchQuery && 'adapt_small_screen'}`}>
                <Dropdown.Toggle id="doc-button-toggle">
                    <SvgIcon icon={Icons.MENU} id={'icon-header-menu'}/>
                </Dropdown.Toggle>
                <Dropdown.Menu id={'doc-button-menu'}>
                    {renderButtons()}
                </Dropdown.Menu>
            </Dropdown>
            <div className={`big_screen_only ${hasSearchQuery ? 'disabled_small_screen' : ''}`}>
                {renderButtons()}
            </div>
        </>
    );

};
